import { reactive, ref, watch } from 'vue'
import { globalRules } from './rules'
import { ComposerTranslation } from 'vue-i18n'

export type UseValidation = ReturnType<typeof useValidation>

export default function useValidation() {
  let oldModel: {
    [key: string]: ModelValue
  } = {}
  const model = reactive<{
    [key: string]: ModelValue
  }>({})

  const dependsIfMap: Map<string, DependsIfField[]> = new Map()

  const validationSchema = reactive<ValidationSchema>({})
  let globalErrors = reactive<string[]>([])
  const isValid = ref<boolean>(true)

  watch(model, () => {
    Object.keys(model).forEach(key => {
      if (oldModel[key] !== model[key]) {
        handleDependsIf(key, model[key])
      }
    })
    oldModel = { ...model }
  })

  const validateSchema = (
    particularSchema?: ValidationSchema,
    particularModel?: any
  ): boolean => {
    globalErrors = []
    const schemaToValidate = particularSchema || validationSchema
    const modelToValidate = particularModel || model
    Object.keys(schemaToValidate).forEach(key => {
      const modelKey = key.split('@')[0] || key
      if (schemaToValidate[key].fields) {
        if (!Array.isArray(modelToValidate[modelKey])) {
          return {}
        }

        const nestedModel: any = {}
        modelToValidate[modelKey].forEach((item: any, index: number) => {
          if (item && typeof item === 'object') {
            Object.keys(item).forEach(property => {
              nestedModel[property] = item[property]
            })
          }
        })

        return validateSchema(schemaToValidate[key].fields, nestedModel)
      }
      const rules = schemaToValidate[key].rules
      const ignored = schemaToValidate[key].ignoreField
      let errors = []
      if (!ignored) {
        errors = rules?.map((rule: any) => {
          try {
            const params = []
            let message = undefined
            if (typeof rule === 'string') {
              const [ruleName, ruleParamsString = '', ruleMessage] =
                rule.split(':')
              if (ruleParamsString) {
                const ruleParams = ruleParamsString.split(',')
                params.push(...ruleParams)
              }
              if (ruleMessage) {
                message = ruleMessage
              }
              rule = globalRules.get(ruleName)
            }
            rule(...replaceModelParams(params), message).validateSync(
              modelToValidate[modelKey]
            )
            return null
          } catch (error: any) {
            return error.message
          }
        })
      }
      schemaToValidate[key].isValid = errors?.every(
        (error: any) => error === null
      )
      schemaToValidate[key].errors = errors?.filter(
        (error: any) => error !== null
      ) as string[] | []
      globalErrors.push(...schemaToValidate[key].errors)
      isValid.value = globalErrors.length === 0
    })
    return isValid.value
  }

  const replaceModelParams = (params: any[]) => {
    return params.map(param => {
      if (param.startsWith('$')) {
        const key = param.replace('$', '')
        return model[key]
      }
      return param
    })
  }

  const handleValidation = (): boolean => {
    return validateSchema()
  }

  const addValidation = (key: string, rules: string[]) => {
    validationSchema[key] = {
      isValid: false,
      errors: [],
      rules,
      ignoreField: false
    }
  }

  const addDependencyValidation = (
    dependKey: string,
    parentKey: string,
    parentValue: DependsIfFieldValue
  ) => {
    const oldValues = dependsIfMap.get(parentKey) ?? []
    dependsIfMap.set(parentKey, [
      ...oldValues,
      {
        field: dependKey,
        value: parentValue
      }
    ])
  }

  const handleDependsIf = (fieldKey: string, value: ModelValue): void => {
    let dependsIf = dependsIfMap.get(fieldKey) ?? []

    dependsIf.forEach(dependsIfField => {
      validationSchema[dependsIfField.field].ignoreField = !validateIf(
        value,
        dependsIfField.value
      )
    })
  }

  const validateIf = (
    fieldValue: DependsIfFieldValue,
    ifValue: DependsIfFieldValue
  ): boolean => {
    // fieldValue is string and ifValue is array ** someone **
    if (Array.isArray(ifValue) && typeof fieldValue === 'string') {
      return ifValue.some((value: string) => value === fieldValue)
    }

    // fieldValue is array and ifValue is string ** someone **
    if (
      Array.isArray(fieldValue) &&
      fieldValue.every(item => typeof item === 'string') &&
      typeof ifValue === 'string'
    ) {
      return fieldValue.some((value: string) => value === ifValue)
    }

    // fieldValue is array and ifValue is array ** every match  **
    if (Array.isArray(fieldValue) && Array.isArray(ifValue)) {
      const values = ifValue as string[]
      const fieldValues = fieldValue as string[]

      return values.every((value: string) => fieldValues.includes(value))
    }

    // fieldValue is primitive  and ifValue is primitive ** equals **
    return fieldValue.toString() === ifValue.toString()
  }

  const setInitialModel = (initialModel: any) => {
    Object.keys(initialModel).forEach(key => {
      model[key] = initialModel[key]
    })
  }

  const translateErrorMessage = (
    t: ComposerTranslation,
    error: any
  ): string => {
    let result: string[] = []
    if (error && Array.isArray(error)) {
      result = error.map((item: any) => {
        if (item.includes('/')) {
          const [prefix, value] = item.split('//')
          const subValues = value
            .split(',')
            .map((subValue: any) => subValue.trim())
          return t(prefix, subValues)
        } else {
          return t(item)
        }
      })
    }
    return result?.join(', ')
  }

  return {
    isValid,
    model,
    addDependencyValidation,
    validationSchema,
    globalErrors,
    handleValidation,
    addValidation,
    setInitialModel,
    validateIf,
    translateErrorMessage
  }
}
export interface ValidationSchema {
  [key: string]: ValidationSchemaField | any
}
export interface ValidationSchemaField {
  rules: string[]
  isValid: boolean
  errors: string[]
  ignoreField: boolean
}

type BaseValue = string | number | boolean | string[] | object

type DependsIfFieldValue = BaseValue
type ModelValue = BaseValue

interface DependsIfField {
  field: string
  value: DependsIfFieldValue
}
